import { useState } from 'react';
import InvestmentsSummaryTable from '../components/Investments/InvestmentsSummaryTable';
import LoggedInLayout from '../components/LoggedInLayout';
import InvestmentsExposure from '../components/Investments/InvestmentsExposure';
import InvestmentsBarSummary from '../components/Investments/InvestmentsBarSummary';
import useGet from '../hooks/useGet';
import { hasMixedCurrencies, toIlsStatement, RateMode } from '../lib/currency';

export function getShadeColors(count: number, hue = 33, saturation = 85) {
   const minLightness = 45;
   const maxLightness = 80;

   if (count <= 1) {
      return [`hsl(${hue}, ${saturation}%, ${maxLightness}%)`];
   }

   return Array.from({ length: count }, (_, index) => {
      const lightness =
         minLightness + ((maxLightness - minLightness) * index) / (count - 1);
      return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
   });
}

//Will control which widgets to show
export default function InvestmentsView() {
   const [investments, investmentsError] = useGet<`me/statements/current`>(
      `me/statements/current`,
   );
   const closedEndedInvestments = investments?.closedEnded || [];

   const colors = getShadeColors(closedEndedInvestments.length);

   const mixedCurrencies = hasMixedCurrencies(closedEndedInvestments);
   const [rateMode, setRateMode] = useState<RateMode>('statementDate');

   // When investments span multiple currencies, graphs are drawn from the ILS
   // equivalent of each value so the amounts are comparable.
   const graphInvestments = mixedCurrencies
      ? closedEndedInvestments.map(investment =>
           toIlsStatement(investment, rateMode),
        )
      : closedEndedInvestments;

   return (
      <LoggedInLayout>
         <div className="min-h-screen bg-gray-100">
            <div className="mx-4 py-20 md:py-24">
               <div className="max-w-5xl mx-auto">
                  <div className="mb-3 pb-1 md:pb-5 ">
                     <h1 className="mb-5 text-3xl text-left font-bold leading-tight text-gray-900">
                        Investments
                     </h1>
                     {mixedCurrencies && (
                        <div className="mb-4 flex items-center gap-3">
                           <span className="text-xs font-medium uppercase tracking-wider text-gray-500">
                              Currency Conversion
                           </span>
                           <div className="inline-flex rounded-md border border-gray-200 bg-white p-0.5 shadow-sm">
                              <button
                                 type="button"
                                 onClick={() => setRateMode('today')}
                                 className={`rounded px-3 py-1 text-xs font-medium transition-colors ${
                                    rateMode === 'today'
                                       ? 'bg-gray-900 text-white'
                                       : 'text-gray-500 hover:text-gray-900'
                                 }`}
                              >
                                 Today's rate
                              </button>
                              <button
                                 type="button"
                                 onClick={() => setRateMode('statementDate')}
                                 className={`rounded px-3 py-1 text-xs font-medium transition-colors ${
                                    rateMode === 'statementDate'
                                       ? 'bg-gray-900 text-white'
                                       : 'text-gray-500 hover:text-gray-900'
                                 }`}
                              >
                                 Statement date rate
                              </button>
                           </div>
                        </div>
                     )}
                     <div className="-mx-4 sm:mx-0 overflow-hidden shadow rounded-lg">
                        <div className="max-w-6xl">
                           <div className="flex flex-col">
                              <div className="align-middle min-w-full shadow overflow-hidden sm:rounded-lg">
                                 <InvestmentsSummaryTable
                                    investments={closedEndedInvestments}
                                    showIls={mixedCurrencies}
                                    rateMode={rateMode}
                                 />
                              </div>
                           </div>
                        </div>
                     </div>
                     <div className="-mx-4 sm:mx-0 overflow-hidden shadow rounded-lg mt-4">
                        <div className="max-w-6xl">
                           <div className="flex flex-col">
                              <div className="align-middle min-w-full shadow overflow-hidden sm:rounded-lg">
                                 <div className="px-6 py-4 text-lg font-medium text-gray-500 text-xs uppercase bg-gray-50 border-b border-gray-200">
                                    Investments Summary
                                 </div>
                                 <InvestmentsBarSummary
                                    investments={graphInvestments}
                                 />
                              </div>
                           </div>
                        </div>
                     </div>
                     <div className="max-w-6xl -mx-4 sm:mx-0">
                        <div className="flex flex-wrap md:flex-row gap-0 sm:gap-4">
                           <div className="flex-1 min-w-[300px] mt-4">
                              <InvestmentsExposure
                                 investments={graphInvestments}
                                 dataType="totalCommitment"
                                 title="Commitment"
                                 colors={colors}
                              />
                           </div>
                           <div className="flex-1 min-w-[300px] mt-4">
                              <InvestmentsExposure
                                 investments={graphInvestments}
                                 dataType="capitalCalled"
                                 title="Total Contributions"
                                 colors={colors}
                              />
                           </div>
                           <div className="flex-1 min-w-[300px] mt-4">
                              <InvestmentsExposure
                                 investments={closedEndedInvestments}
                                 dataType="nav"
                                 title="Net Asset Value"
                                 colors={colors}
                              />
                           </div>
                        </div>
                     </div>
                  </div>
               </div>
            </div>
         </div>
      </LoggedInLayout>
   );
}
